import { NOTE_HTML_HREF } from '@crm/contracts';
import { BackgroundColor, Color, TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../../lib/utils.ts';
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx';

/** The highlights offered; `#f1c40f` is the yellow the teams' note templates use. */
const HIGHLIGHTS = [
  { value: '#f1c40f', name: 'Yellow' },
  { value: '#a3e635', name: 'Green' },
  { value: '#7dd3fc', name: 'Blue' },
  { value: '#f9a8d4', name: 'Pink' },
  { value: '#fdba74', name: 'Orange' },
] as const;
const TEXT_COLOURS = [
  { value: '#dc2626', name: 'Red' },
  { value: '#16a34a', name: 'Green' },
  { value: '#2563eb', name: 'Blue' },
  { value: '#9333ea', name: 'Purple' },
  { value: '#ea580c', name: 'Orange' },
] as const;

/** The same content an `html` note keeps (contracts `NOTE_HTML_TAGS`): no headings, code or quotes. */
const EXTENSIONS = [
  StarterKit.configure({
    heading: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
  }),
  TextStyle,
  Color,
  BackgroundColor,
];

/** True when the HTML has no text a reader would see. */
export const isBlankHtml = (html: string) => html.replace(/<[^>]*>|&nbsp;|\s/g, '') === '';

function ToolButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active ?? false}
      title={label}
      // Keep the selection in the editor: a mousedown would move focus to the button.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring inline-flex size-7 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none [&_svg]:size-3.5',
        active && 'bg-muted text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Swatches({
  label,
  colours,
  onPick,
  onClear,
}: {
  label: string;
  colours: ReadonlyArray<{ value: string; name: string }>;
  onPick: (colour: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
        {label}
      </span>
      <div className="flex items-center gap-1">
        {colours.map((c) => (
          <button
            key={c.value}
            type="button"
            aria-label={`${label} ${c.name}`}
            title={c.name}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(c.value)}
            className="border-border focus-visible:ring-ring size-5 rounded border focus-visible:ring-2 focus-visible:outline-none"
            style={{ backgroundColor: c.value }}
          />
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground ml-1 text-xs"
        >
          None
        </button>
      </div>
    </div>
  );
}

function Toolbar({ editor, extra }: { editor: Editor; extra?: ReactNode }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      link: e.isActive('link'),
    }),
  });
  const chain = () => editor.chain().focus();
  const link = () => {
    if (state.link) return chain().unsetLink().run();
    const typed = window.prompt('Link address')?.trim();
    if (!typed) return;
    // A bare `acme.com` is a web address; any other scheme the API would drop on save.
    const href = /^[a-z][a-z\d+.-]*:/i.test(typed) ? typed : `https://${typed}`;
    if (!NOTE_HTML_HREF.test(href))
      return window.alert('A link must start with https://, http://, mailto: or tel:');
    chain().extendMarkRange('link').setLink({ href }).run();
  };
  return (
    <div className="border-border flex flex-wrap items-center gap-0.5 border-b px-1 py-0.5">
      <ToolButton label="Bold" active={state.bold} onClick={() => chain().toggleBold().run()}>
        <Bold />
      </ToolButton>
      <ToolButton label="Italic" active={state.italic} onClick={() => chain().toggleItalic().run()}>
        <Italic />
      </ToolButton>
      <ToolButton
        label="Underline"
        active={state.underline}
        onClick={() => chain().toggleUnderline().run()}
      >
        <Underline />
      </ToolButton>
      <ToolButton
        label="Strikethrough"
        active={state.strike}
        onClick={() => chain().toggleStrike().run()}
      >
        <Strikethrough />
      </ToolButton>
      <ToolButton
        label="Bulleted list"
        active={state.bullet}
        onClick={() => chain().toggleBulletList().run()}
      >
        <List />
      </ToolButton>
      <ToolButton
        label="Numbered list"
        active={state.ordered}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <ListOrdered />
      </ToolButton>
      <ToolButton label={state.link ? 'Remove link' : 'Link'} active={state.link} onClick={link}>
        <LinkIcon />
      </ToolButton>
      <Popover>
        <PopoverTrigger
          aria-label="Colour"
          title="Colour"
          onMouseDown={(e) => e.preventDefault()}
          className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex size-7 items-center justify-center rounded-md [&_svg]:size-3.5"
        >
          <Highlighter />
        </PopoverTrigger>
        <PopoverContent align="start" className="flex w-auto flex-col gap-2 p-2">
          <Swatches
            label="Highlight"
            colours={HIGHLIGHTS}
            onPick={(c) => chain().setBackgroundColor(c).run()}
            onClear={() => chain().unsetBackgroundColor().run()}
          />
          <Swatches
            label="Text"
            colours={TEXT_COLOURS}
            onPick={(c) => chain().setColor(c).run()}
            onClear={() => chain().unsetColor().run()}
          />
        </PopoverContent>
      </Popover>
      {extra && <span className="ml-auto flex items-center gap-1">{extra}</span>}
    </div>
  );
}

/**
 * The rich text editor for notes and note templates: Tiptap, writing the HTML the API
 * keeps (`NOTE_HTML_TAGS`). `value` is read once, when it mounts (remount with a `key` to load
 * another); every change goes to `onChange` as HTML. `onEditor` hands the editor to the caller,
 * so "Insert template" and "Insert field" can insert at the cursor. `toolbar` adds controls at
 * the right of the toolbar.
 */
export function RichTextEditor({
  value,
  onChange,
  label,
  autoFocus,
  className,
  toolbar,
  onEditor,
  onSubmit,
}: {
  value: string;
  onChange: (html: string) => void;
  label: string;
  autoFocus?: boolean | undefined;
  className?: string | undefined;
  toolbar?: ReactNode;
  onEditor?: ((editor: Editor | null) => void) | undefined;
  /** Ctrl/Cmd+Enter. */
  onSubmit?: (() => void) | undefined;
}) {
  const submit = useRef(onSubmit);
  useEffect(() => {
    submit.current = onSubmit;
  }, [onSubmit]);
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        'aria-label': label,
        'aria-multiline': 'true',
        role: 'textbox',
        class: 'rich-text min-h-16 px-3 py-2 text-sm leading-relaxed outline-none',
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && submit.current) {
          submit.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
  });
  useEffect(() => {
    onEditor?.(editor);
    return () => onEditor?.(null);
  }, [editor, onEditor]);
  return (
    <div
      className={cn(
        'border-input bg-background focus-within:ring-ring/50 flex flex-col rounded-md border focus-within:ring-2',
        className,
      )}
      data-testid="rich-text"
    >
      {editor && <Toolbar editor={editor} extra={toolbar} />}
      <EditorContent
        editor={editor}
        className="min-h-0 flex-1 cursor-text overflow-y-auto"
        // A click on the empty room below the text puts the caret at its end.
        onMouseDown={(e) => {
          if (e.target !== e.currentTarget) return;
          e.preventDefault();
          editor?.commands.focus('end');
        }}
      />
    </div>
  );
}
